import { useState, useCallback } from 'react';
import { useList, useCustom, useApiUrl } from '@refinedev/core';
import { formatDate } from '@/lib/utils';
import { SortableHeader, nextSort, type SortOrder } from '@/components/table/SortableHeader';
import {
  ShieldCheck,
  ShieldAlert,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { getIdToken } from '@/providers/auth-provider';

interface QueueItem {
  user_id: string;
  email: string;
  display_name: string | null;
  provider_overall_status: string;
  mismatch_tags: string[];
  fraud_tags: string[];
  submitted_at: string;
  trust_score: number;
}

interface VerificationDetail {
  user_id: string;
  email: string;
  display_name: string | null;
  created_at: string;
  provider_overall_status: string;
  trust_score: number;
  mismatch_tags: string[];
  fraud_tags: string[];
  auto_face: string | null;
  auto_document: string | null;
  doc_data: {
    full_name: string | null;
    doc_dob: string | null;
    doc_nationality: string | null;
    doc_type: string | null;
    doc_number: string | null;
    doc_expiry: string | null;
  };
  user_birthday: string | null;
  session_id: string;
  submitted_at: string;
  scan_ref: string;
}

const mismatchLabels: Record<string, string> = {
  DATE_OF_BIRTH: 'Date of Birth',
  NAME: 'Name',
  DOCUMENT_NUMBER: 'Document Number',
  EXPIRY_DATE: 'Expiry Date',
  NATIONALITY: 'Nationality',
};

function ReviewDetail({
  userId,
  onBack,
  onResolved,
}: {
  userId: string;
  onBack: () => void;
  onResolved: () => void;
}) {
  const apiUrl = useApiUrl();
  const [resolving, setResolving] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: detailData, isLoading } = useCustom<VerificationDetail>({
    url: `/v1/admin/verification/${userId}`,
    method: 'get',
  });

  const detail = detailData?.data ?? null;

  const handleResolve = useCallback(async (decision: 'approve' | 'reject') => {
    setResolving(true);
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${apiUrl}/v1/admin/verification/${userId}/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ decision, notes: notes || undefined }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onResolved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  }, [apiUrl, userId, notes, onResolved]);

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Queue
      </button>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : detail ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Verification Review</h2>
              <p className="text-muted-foreground">{detail.email}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800">
              <ShieldAlert className="h-4 w-4" />
              {detail.provider_overall_status}
            </span>
          </div>

          {error && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive text-sm">
              {error}
            </div>
          )}

          {/* User Info */}
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">User</div>
              <div className="mt-1 font-medium">{detail.display_name || 'No name'}</div>
              <div className="text-sm text-muted-foreground">{detail.email}</div>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Account Created</div>
              <div className="mt-1 font-medium">{formatDate(detail.created_at)}</div>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Trust Score</div>
              <div className="mt-1 text-2xl font-bold">{detail.trust_score}</div>
            </div>
          </div>

          {/* Verification Results */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm font-medium text-muted-foreground mb-2">Automated Checks</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Face Match</span>
                  <span className={`text-sm font-medium ${detail.auto_face === 'FACE_MATCH' ? 'text-green-600' : 'text-red-600'}`}>
                    {detail.auto_face || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Document</span>
                  <span className={`text-sm font-medium ${detail.auto_document === 'DOC_VALIDATED' ? 'text-green-600' : 'text-red-600'}`}>
                    {detail.auto_document || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm font-medium text-muted-foreground mb-2">Flags</div>
              <div className="space-y-2">
                {detail.mismatch_tags.length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Data Mismatches</div>
                    <div className="flex flex-wrap gap-1">
                      {detail.mismatch_tags.map((tag) => (
                        <span key={tag} className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          {mismatchLabels[tag] || tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {detail.fraud_tags.length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Fraud Indicators</div>
                    <div className="flex flex-wrap gap-1">
                      {detail.fraud_tags.map((tag) => (
                        <span key={tag} className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {detail.mismatch_tags.length === 0 && detail.fraud_tags.length === 0 && (
                  <span className="text-sm text-muted-foreground">No flags</span>
                )}
              </div>
            </div>
          </div>

          {/* Data Comparison */}
          <div className="rounded-lg border bg-card p-4">
            <div className="text-sm font-medium text-muted-foreground mb-3">Data Comparison</div>
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="pb-2 text-left text-xs font-medium text-muted-foreground">Field</th>
                  <th className="pb-2 text-left text-xs font-medium text-muted-foreground">User Profile</th>
                  <th className="pb-2 text-left text-xs font-medium text-muted-foreground">ID Document</th>
                  <th className="pb-2 text-left text-xs font-medium text-muted-foreground">Match</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b">
                  <td className="py-2 text-muted-foreground">Name</td>
                  <td className="py-2">{detail.display_name || '—'}</td>
                  <td className="py-2">{detail.doc_data.full_name || '—'}</td>
                  <td className="py-2">
                    {detail.mismatch_tags.includes('NAME')
                      ? <XCircle className="h-4 w-4 text-red-500" />
                      : <CheckCircle className="h-4 w-4 text-green-500" />}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 text-muted-foreground">Date of Birth</td>
                  <td className="py-2">{detail.user_birthday || '—'}</td>
                  <td className="py-2">{detail.doc_data.doc_dob || '—'}</td>
                  <td className="py-2">
                    {detail.mismatch_tags.includes('DATE_OF_BIRTH')
                      ? <XCircle className="h-4 w-4 text-red-500" />
                      : <CheckCircle className="h-4 w-4 text-green-500" />}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 text-muted-foreground">Nationality</td>
                  <td className="py-2">—</td>
                  <td className="py-2">{detail.doc_data.doc_nationality || '—'}</td>
                  <td className="py-2"><CheckCircle className="h-4 w-4 text-green-500" /></td>
                </tr>
                <tr>
                  <td className="py-2 text-muted-foreground">Document</td>
                  <td className="py-2">—</td>
                  <td className="py-2">{detail.doc_data.doc_type || '—'} {detail.doc_data.doc_number ? `(${detail.doc_data.doc_number})` : ''}</td>
                  <td className="py-2"><CheckCircle className="h-4 w-4 text-green-500" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Decision */}
          <div className="rounded-lg border bg-card p-4 space-y-4">
            <div className="text-sm font-medium">Decision</div>
            <div>
              <label className="text-sm text-muted-foreground">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-lg border bg-background p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Reason for decision..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => handleResolve('approve')}
                disabled={resolving}
                className="flex items-center gap-2 rounded-lg bg-green-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Approve
              </button>
              <button
                onClick={() => handleResolve('reject')}
                disabled={resolving}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                Reject
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function VerificationQueuePage() {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [trustMax, setTrustMax] = useState('');
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const handleSort = (field: string) => {
    const next = nextSort({ field: sortField, order: sortOrder }, field);
    setSortField(next.field);
    setSortOrder(next.order);
  };

  const { data, isLoading, isError, refetch } = useList<QueueItem>({
    resource: 'verification/queue',
    pagination: { current: 1, pageSize: 100 },
    filters: trustMax ? [{ field: 'trust_score_max', operator: 'eq' as const, value: trustMax }] : [],
    sorters: sortField ? [{ field: sortField, order: sortOrder }] : [],
  });

  const queue = data?.data ?? [];

  if (selectedUserId) {
    return (
      <ReviewDetail
        userId={selectedUserId}
        onBack={() => setSelectedUserId(null)}
        onResolved={() => {
          setSelectedUserId(null);
          refetch();
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
        Failed to load verification queue. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Verification Review</h2>
        <p className="text-muted-foreground">
          Review identity verifications flagged for manual review
        </p>
      </div>

      {/* Risk-triage filter */}
      <div className="flex items-center gap-2">
        <label className="text-sm text-muted-foreground">Max trust score:</label>
        <select
          value={trustMax}
          onChange={(e) => setTrustMax(e.target.value)}
          aria-label="Filter by maximum trust score"
          className="h-9 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Any</option>
          <option value="25">≤ 25 (highest risk)</option>
          <option value="50">≤ 50</option>
          <option value="75">≤ 75</option>
        </select>
      </div>

      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Flags</th>
                <SortableHeader field="trust_score" label="Trust Score" activeField={sortField} activeOrder={sortOrder} onSort={handleSort} />
                <SortableHeader field="submitted_at" label="Submitted" activeField={sortField} activeOrder={sortOrder} onSort={handleSort} />
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Action</th>
              </tr>
            </thead>
            <tbody>
              {queue.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <ShieldCheck className="h-12 w-12 mx-auto text-green-500 mb-3" />
                    <div className="text-muted-foreground">No verifications pending review</div>
                  </td>
                </tr>
              ) : (
                queue.map((item) => (
                  <tr key={item.user_id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div className="font-medium">{item.display_name || 'No name'}</div>
                      <div className="text-xs text-muted-foreground">{item.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {item.mismatch_tags.map((tag) => (
                          <span key={tag} className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            {mismatchLabels[tag] || tag}
                          </span>
                        ))}
                        {item.fraud_tags.map((tag) => (
                          <span key={tag} className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                            <AlertTriangle className="h-3 w-3 mr-0.5" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{item.trust_score}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(item.submitted_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelectedUserId(item.user_id)}
                        className="rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
